import TimeConstants from '@module/utils/item';
import type { MediaPlayer } from 'amazon-ivs-player';
import { create, type StateCreator } from 'zustand';
import {
  ivsQualitySafeLocalStorage,
  plyrStateSafeLocalStorage,
  rtIvsQualitySafeLocalStorage,
} from '../lib/storage/storageImplementations';

const isMediaPlayer = (player: unknown): player is MediaPlayer => {
  return !!player && typeof (player as MediaPlayer).setQuality === 'function';
};

const DEFAULT_TOOLBAR_AUTO_HIDE_MS = TimeConstants.ONE_SECOND * 5;
const storedPlyrState = plyrStateSafeLocalStorage.get();
const INITIAL_VOLUME = storedPlyrState?.volume ?? 0;
const INITIAL_MUTED = (storedPlyrState?.volume === 0 || storedPlyrState?.muted) ?? true;

type ToolbarVisibilityUpdater = boolean | ((prev: boolean) => boolean);
type ToolbarVisibilityOptions = {
  autoHideMs?: number;
};

type RTQualityHandlers = {
  selectQuality: (quality: Quality) => void;
  selectAutoQuality: () => void;
} | null;
type RTSessionCleanup = (() => void) | null;

export type Quality = {
  bitrate: number;
  codecs: string;
  framerate: number;
  group: string;
  height: number;
  isDefault: boolean;
  name: string;
  width: number;
};

type PlayerStatus = 'playing' | 'paused' | 'buffering' | 'error';
interface LocalPlayerState {
  player: MediaPlayer | HTMLVideoElement | null;
  setPlayer: (player: MediaPlayer | HTMLVideoElement | null) => void;

  status: PlayerStatus;
  volume: number;
  muted: boolean;
  autoQualityMode: boolean;
  quality: Quality | null;
  qualities: Quality[];
  isPlayerFullscreen: boolean;
  isPipMode: boolean;
  isPlayerToolbarVisible: boolean;
  rtQualityHandlers: RTQualityHandlers;
  rtSessionCleanup: RTSessionCleanup;

  play: () => void | Promise<void>;
  pause: () => void;
  dispose: () => void; // 플레이어별 초기화 지점에서 각각 설정되어야 함.
  setStatus: (status: PlayerStatus) => void;
  setVolume: (volume: number) => void;
  setMuted: (muted: boolean, options?: { persist?: boolean }) => void;
  /**
   * 자동 화질 모드로 전환 (사용자 액션).
   * RT/LL 분기 + localStorage 제거 + autoQualityMode=true.
   */
  setAutoQualityMode: () => void;
  /**
   * onlyState=false (수동 선택, 사용자 액션):
   *   RT/LL 분기 + localStorage 저장 + state(quality + autoQualityMode=false).
   * onlyState=true (플레이어 이벤트 동기화):
   *   side effect 없이 quality 상태만 갱신.
   */
  setQuality: (payload: { quality: Quality | null; onlyState?: boolean }) => void;
  setQualities: (qualities: Quality[]) => void;
  setRTQualityHandlers: (handlers: RTQualityHandlers) => void;
  setRTSessionCleanup: (cleanup: RTSessionCleanup) => void;
  togglePlayerFullscreen: () => void;
  setIsPipMode: (val: boolean) => void;
  setIsPlayerToolbarVisible: (updater: ToolbarVisibilityUpdater, options?: ToolbarVisibilityOptions) => void;
}

const store: StateCreator<LocalPlayerState> = (set, get) => {
  let toolbarTimeout: NodeJS.Timeout | null = null;

  const clearToolbarTimeout = () => {
    if (toolbarTimeout) {
      clearTimeout(toolbarTimeout);
      toolbarTimeout = null;
    }
  };

  const scheduleToolbarAutoHide = (autoHideMs = DEFAULT_TOOLBAR_AUTO_HIDE_MS) => {
    toolbarTimeout = setTimeout(() => {
      set((state) => ({
        ...state,
        isPlayerToolbarVisible: false,
      }));
      toolbarTimeout = null;
    }, autoHideMs);
  };

  // Handle fullscreen changes from external sources (e.g., ESC key)
  const handleFullscreenChange = () => {
    const isCurrentlyFullscreen = !!document.fullscreenElement;
    if (get().isPlayerFullscreen !== isCurrentlyFullscreen) {
      set({ isPlayerFullscreen: isCurrentlyFullscreen });
    }
  };

  // Set up fullscreen change listener
  if (typeof window !== 'undefined') {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
  }

  return {
    player: null,
    setPlayer: (player: MediaPlayer | HTMLVideoElement | null) => {
      const { volume, muted } = get();

      if (isMediaPlayer(player)) {
        player.setVolume(volume);
        player.setMuted(muted);
      } else if (player instanceof HTMLVideoElement) {
        player.volume = volume;
        player.muted = muted;
      }
      set((state) => ({ ...state, player }));
    },

    status: 'buffering',
    volume: INITIAL_MUTED ? 0 : INITIAL_VOLUME,
    muted: INITIAL_MUTED,
    autoQualityMode: true,
    quality: null,
    qualities: [],
    rtQualityHandlers: null,
    rtSessionCleanup: null,
    isPlayerFullscreen: false,
    isPipMode: false,
    isPlayerToolbarVisible: false,

    play: () => {
      const player = get().player;
      if (player instanceof HTMLVideoElement) {
        player
          .play()
          .then(() => {
            set((state) => ({ ...state, status: 'playing' }));
          })
          .catch((error) => {
            console.error('[Player] Failed to play:', error);
            set((state) => ({ ...state, status: 'error' }));
          });
      } else if (isMediaPlayer(player)) {
        player.play();
        set((state) => ({ ...state, status: 'playing' }));
      }
    },
    pause: () => {
      const player = get().player;
      player?.pause();
      set((state) => ({ ...state, status: 'paused' }));
    },
    dispose: () => {
      const { player, rtSessionCleanup } = get();
      rtSessionCleanup?.();
      if (isMediaPlayer(player)) {
        player.delete();
      } else if (player instanceof HTMLVideoElement) {
        player.pause();
        if (player.srcObject instanceof MediaStream) {
          player.srcObject.getTracks().forEach((track) => track.stop());
        }
        player.srcObject = null;
      }
      // Clean up fullscreen listener
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      clearToolbarTimeout();
      set((state) => ({
        ...state,
        player: null,
        status: 'buffering',
        autoQualityMode: true,
        quality: null,
        qualities: [],
        isPlayerFullscreen: false,
        isPipMode: false,
        isPlayerToolbarVisible: false,
        rtQualityHandlers: null,
        rtSessionCleanup: null,
      }));
    },
    setStatus: (status: PlayerStatus) => set((state) => ({ ...state, status })),
    setVolume: (volume: number) => {
      const player = get().player;
      if (isMediaPlayer(player)) {
        player.setVolume(volume);
        player.setMuted(volume === 0);
      } else if (player instanceof HTMLVideoElement) {
        player.volume = volume;
        player.muted = volume === 0;
      }

      plyrStateSafeLocalStorage.set({
        muted: volume === 0,
        volume,
      });
      set((state) => ({ ...state, muted: volume === 0, volume }));
    },
    setMuted: (muted: boolean, options?: { persist?: boolean }) => {
      const { player, volume: currentVolume } = get();

      // unmute인데 현재 볼륨이 0일 때만 마지막 저장 볼륨으로 복원, 그 외엔 현재 볼륨 유지.
      const nextVolume = !muted && currentVolume === 0 ? plyrStateSafeLocalStorage.get()?.volume || 0.5 : currentVolume;

      if (isMediaPlayer(player)) {
        player.setMuted(muted);
        if (!muted) player.setVolume(nextVolume);
      } else if (player instanceof HTMLVideoElement) {
        player.muted = muted;
        if (!muted) player.volume = nextVolume;
      }
      // options.persist=false: 런타임 음소거만 바꾸고 저장된 음소거 기록은 보존 (블라인드 등 강제/프로그래매틱 음소거용).
      // 미전달 시 저장(사용자 직접 조작 = 기록 갱신). useOldPlayerStore.setIsMute 와 동일 관용구.
      if (options?.persist !== false) {
        plyrStateSafeLocalStorage.set({ muted, volume: nextVolume });
      }
      set((state) => ({ ...state, muted, volume: nextVolume }));
    },
    setAutoQualityMode: () => {
      const { player, rtQualityHandlers } = get();
      if (rtQualityHandlers) {
        rtQualityHandlers.selectAutoQuality();
        rtIvsQualitySafeLocalStorage.remove();
      } else if (isMediaPlayer(player)) {
        player.setAutoQualityMode(true);
        ivsQualitySafeLocalStorage.remove();
      }
      set((state) => ({ ...state, autoQualityMode: true }));
    },
    setQuality: ({ quality, onlyState }) => {
      const { player, rtQualityHandlers } = get();
      const isManualSelection = !onlyState && !!quality;

      if (isManualSelection) {
        if (rtQualityHandlers) {
          rtQualityHandlers.selectQuality(quality);
          rtIvsQualitySafeLocalStorage.set(quality);
        } else if (isMediaPlayer(player)) {
          player.setQuality(quality);
          ivsQualitySafeLocalStorage.set(quality);
        }
      }

      set((state) => ({
        ...state,
        quality,
        ...(isManualSelection && { autoQualityMode: false }),
      }));
    },
    setQualities: (qualities: Quality[]) => {
      const getResolution = (name: string): number => {
        const match = name.match(/(\d+)p/);
        return match ? parseInt(match[1], 10) : -1;
      };

      const allParseable = qualities.length > 0 && qualities.every((q) => getResolution(q.name) !== -1);
      // 간헐적으로 압축 효율 때문에 실제 1080p이 고화질임에도 비트레이트가 낮게 와 순서가 뒤로 밀리는 경우가 존재.
      // 서버 name이 변경되어 파싱이 정상적이 안된다면 기본값을 그대로 전달
      const sortedQualities = allParseable
        ? [...qualities].sort((a, b) => getResolution(b.name) - getResolution(a.name))
        : qualities;

      set((state) => ({ ...state, qualities: sortedQualities }));
    },
    setRTQualityHandlers: (handlers) => {
      set((state) => ({ ...state, rtQualityHandlers: handlers }));
    },
    setRTSessionCleanup: (cleanup) => {
      set((state) => ({ ...state, rtSessionCleanup: cleanup }));
    },
    togglePlayerFullscreen: () => {
      const contentSection = document.getElementById('player-area');
      if (!get().isPlayerFullscreen && contentSection) {
        contentSection.requestFullscreen();
        set({ isPlayerFullscreen: true });
      } else {
        document.exitFullscreen();
        set({ isPlayerFullscreen: false });
      }
    },
    setIsPipMode: (val) =>
      set((state) => ({
        ...state,
        isPipMode: val,
      })),
    setIsPlayerToolbarVisible: (updater, options) => {
      clearToolbarTimeout();
      const nextValue = typeof updater === 'function' ? updater(get().isPlayerToolbarVisible ?? false) : updater;

      set((state) => ({
        ...state,
        isPlayerToolbarVisible: nextValue,
      }));

      if (nextValue) {
        scheduleToolbarAutoHide(options?.autoHideMs);
      }
    },
  };
};

const usePlayerStore = create<LocalPlayerState>()(store);

export default usePlayerStore;
